import { useState, type ReactNode } from 'react'
import { Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { useStopWork } from './api'

/**
 * Stopping is the moment to say what got done. It is the work log: one line per stretch, kept with
 * the session, shown on the item. Never required — "Stop" alone is always one click.
 */
export function StopWorkPopover({ children }: { children: ReactNode }) {
  const stop = useStopWork()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const submit = (withNote: boolean) =>
    stop.mutate(withNote ? note.trim() || undefined : undefined, {
      onSuccess: () => {
        setOpen(false)
        setNote('')
      },
    })
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-1.5 text-sm font-semibold">What did you get done?</p>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          rows={3}
          autoFocus
          placeholder="A line for the work log (optional)"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(true)
          }}
        />
        <div className="mt-2 flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" disabled={stop.isPending} onClick={() => submit(false)}>
            Stop without note
          </Button>
          <Button type="button" size="sm" disabled={stop.isPending} onClick={() => submit(true)}>
            <Square className="size-3" fill="currentColor" /> Stop work
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
